/* Copyright (c) 2024 MOCA Systems, Inc. All rights reserved. */

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subject, takeUntil, withLatestFrom } from 'rxjs';

import { MaterialModule } from 'src/app/common/material/material-module';
import { PlanTicketsFilterRemoteControlService } from 'src/app/services/plan-tickets-filter-remote-control.service';
import { SharedUIMaterialModule } from 'src/app/shared/ui/material/shared-ui-material.module';

const MODULES = [CommonModule, MaterialModule, ReactiveFormsModule, SharedUIMaterialModule];

@Component({
	standalone: true,
	selector: 'tp-quick-search',
	templateUrl: './quick-search.component.html',
	styleUrls: ['./quick-search.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [...MODULES],
	providers: [],
})
export class QuickSearchComponent implements OnInit, OnDestroy {
	private planTicketsFilterRemoteControlService = inject(PlanTicketsFilterRemoteControlService);
	private _onDestroy$ = new Subject<void>();
	public searchControl = new FormControl<string>('');

	constructor() {}

	private listenSearchChanges(): void {
		this.searchControl.valueChanges
			.pipe(
				withLatestFrom(this.planTicketsFilterRemoteControlService.openPlanTicketsFilter$),
				takeUntil(this._onDestroy$)
			)
			.subscribe(([searchValue, isPlanFilterOpen]) => {
				this.planTicketsFilterRemoteControlService.updateQuickSearchValue(searchValue);

				// If is not open and search value is not empty and has at least 1 character then open the filter
				if (!isPlanFilterOpen && searchValue !== '' && searchValue.length >= 1) {
					this.planTicketsFilterRemoteControlService.openPlanTicketsFilter();
					// If quick search is disabled is because the user changed from quick search mode to full filter mode so do no close the filter
				} else if (isPlanFilterOpen && searchValue === '' && !this.searchControl.disabled) {
					this.planTicketsFilterRemoteControlService.closePlanTicketsFilter();
				}
			});
	}

	private listenPlanFilterChanges(): void {
		this.planTicketsFilterRemoteControlService.openPlanTicketsFilter$
			.pipe(takeUntil(this._onDestroy$))
			.subscribe((isOpen) => {
				if (!isOpen && this.searchControl.value !== '') {
					this.clearFilter();
				}
			});
	}

	public clearFilter(emitEventToModel = true): void {
		this.searchControl.setValue('', { emitEvent: emitEventToModel });
	}

	private listenDisableQuickSearch(): void {
		this.planTicketsFilterRemoteControlService.disableQuickSearch$
			.pipe(takeUntil(this._onDestroy$))
			.subscribe((isDisabled) => {
				this.searchControl[isDisabled ? 'disable' : 'enable']();
				this.clearFilter();
			});
	}

	ngOnInit(): void {
		this.listenSearchChanges();
		this.listenPlanFilterChanges();
		this.listenDisableQuickSearch();
	}

	ngOnDestroy(): void {
		this._onDestroy$.next();
		this._onDestroy$.complete();
	}
}
